import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { BarChart3, RefreshCw, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useProfile } from '@/api/profile'
import { useTeamPerformance } from '@/api/teamPerformance'
import type { TeamPerformancePeriod } from '@/types/teamPerformance'
import { PeriodSelector } from './analytics/PeriodSelector'
import { TeamTotalsCards } from './analytics/TeamTotalsCards'
import { AgentLeaderboardTable } from './analytics/AgentLeaderboardTable'
import { TeamHeatmap } from './analytics/TeamHeatmap'
import { usePlanRights } from '@/hooks/usePlanRights'
import { APERCU_ANALYTICS } from './analytics/apercuDemo'
import { PlanBadge } from '@/components/ui/PlanBadge'

/**
 * Vue Analytics côté manager.
 *
 * Différente de l'AnalyticsTab agent (qui montre les stats personnelles de
 * l'agent connecté). Ici on agrège toute l'équipe — le manager est compté
 * parmi les agents s'il prend des appels lui-même (règle d'attribution
 * canonique côté backend, cf. mémoire project-agent-attribution-reliable).
 */
export function AnalyticsView() {
  const { t } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const { data: profile } = useProfile()
  const companyId = profile?.company
  const { allowed, planFor } = usePlanRights()
  const analyticsPermis = allowed('analytics_equipe')

  // La période est synchronisée à ?period= pour que le bouton retour depuis le
  // drilldown conserve le contexte (sinon l'utilisateur retombe sur 'cette_semaine').
  const [searchParams, setSearchParams] = useSearchParams()
  const initialPeriod = (searchParams.get('period') as TeamPerformancePeriod) || 'cette_semaine'
  const [period, setPeriodState] = useState<TeamPerformancePeriod>(initialPeriod)
  const setPeriod = (next: TeamPerformancePeriod) => {
    setPeriodState(next)
    const sp = new URLSearchParams(searchParams)
    if (next === 'cette_semaine') sp.delete('period')
    else sp.set('period', next)
    setSearchParams(sp, { replace: true })
  }
  // Si l'URL change (back navigation), resynchroniser le state local.
  useEffect(() => {
    const fromUrl = searchParams.get('period') as TeamPerformancePeriod | null
    if (fromUrl && fromUrl !== period) setPeriodState(fromUrl)
    else if (!fromUrl && period !== 'cette_semaine') setPeriodState('cette_semaine')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // Le clic sur un nom d'agent ouvre une page dédiée pleine largeur
  // (cohérent avec les drilldowns chiffres). La période courante est préservée.
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const navigate = useNavigate()
  const openAgentPage = (userId: number) => {
    navigate(`/${locale}/manager/agents/${userId}/analytics?period=${period}`)
  }
  const { data, isLoading, isFetching, error, refetch } = useTeamPerformance(
    companyId,
    { period },
  )

  const pageBg = darkMode ? 'bg-slate-900' : 'bg-slate-50'
  const titleColor = darkMode ? 'text-white' : 'text-slate-900'
  const subtitleColor = darkMode ? 'text-slate-400' : 'text-slate-500'

  return (
    <div className={cn('h-full flex flex-col', pageBg)}>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className={cn('text-2xl font-semibold flex items-center gap-2', titleColor)}>
                <BarChart3 className="h-6 w-6 text-indigo-500" />
                {t('manager.analytics.sectionTitle')}
              </h1>
              <p className={cn('text-sm mt-1', subtitleColor)}>
                {t('manager.analytics.sectionSubtitle')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <PeriodSelector value={period} onChange={setPeriod} darkMode={darkMode} />
              <button
                type="button"
                onClick={() => void refetch()}
                disabled={isFetching}
                className={cn(
                  'inline-flex items-center justify-center h-9 w-9 rounded-lg border',
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500/40 disabled:opacity-50',
                  darkMode
                    ? 'border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
                )}
                title={t('manager.analytics.refresh')}
              >
                <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
              </button>
            </div>
          </div>

          {/* Verrou de plan : analytics d'équipe à partir de Team.
              On montre le squelette de la page derrière le message, pour
              donner une idée de ce qu'on débloque. */}
          {!analyticsPermis && (
            <div className="relative">
              <div className="space-y-6 pointer-events-none select-none blur-[3px] opacity-70" aria-hidden="true">
                <TeamTotalsCards
                  current={APERCU_ANALYTICS.team_totals}
                  previous={APERCU_ANALYTICS.team_totals_previous}
                  dailySeries={APERCU_ANALYTICS.daily_series}
                  period="cette_semaine"
                  darkMode={darkMode}
                />
                <AgentLeaderboardTable
                  agents={APERCU_ANALYTICS.agents}
                  period="cette_semaine"
                  darkMode={darkMode}
                  onAgentClick={() => undefined}
                />
                <TeamHeatmap heatmap={APERCU_ANALYTICS.hourly_heatmap} darkMode={darkMode} />
              </div>
              <div
                className={cn(
                  'absolute inset-0 flex items-start justify-center pt-40',
                  darkMode ? 'bg-slate-900/30' : 'bg-slate-50/30',
                )}
              >
                <div
                  className={cn(
                    'sticky top-40 rounded-2xl border px-10 py-8 text-center shadow-xl',
                    darkMode ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white',
                  )}
                >
                  <BarChart3 className="h-10 w-10 mx-auto mb-3 text-indigo-500" />
                  <p className={cn('text-sm mb-4', subtitleColor)}>
                    {t('plan.required', { plan: 'Team' })}
                  </p>
                  <PlanBadge plan={planFor('analytics_equipe')} />
                </div>
              </div>
            </div>
          )}

          {/* État loading */}
          {analyticsPermis && isLoading && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      'h-28 rounded-xl animate-pulse',
                      darkMode ? 'bg-slate-800' : 'bg-slate-200/70',
                    )}
                    style={{ animationDelay: `${i * 60}ms` }}
                  />
                ))}
              </div>
              <div
                className={cn(
                  'h-64 rounded-xl animate-pulse',
                  darkMode ? 'bg-slate-800' : 'bg-slate-200/70',
                )}
              />
            </div>
          )}

          {/* État erreur */}
          {analyticsPermis && error && !isLoading && (
            <div
              className={cn(
                'rounded-xl border p-6 text-center',
                darkMode ? 'border-rose-500/30 bg-rose-500/10' : 'border-rose-200 bg-rose-50',
              )}
            >
              <p className="text-rose-600 dark:text-rose-400 font-medium">
                {error instanceof Error ? error.message : t('manager.analytics.loadError')}
              </p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="mt-2 text-sm text-indigo-500 hover:underline"
              >
                {t('manager.analytics.retry')}
              </button>
            </div>
          )}

          {/* État data */}
          {analyticsPermis && data && !isLoading && (
            <>
              <TeamTotalsCards
                current={data.team_totals}
                previous={data.team_totals_previous}
                dailySeries={data.daily_series}
                period={period}
                darkMode={darkMode}
              />

              <div className="flex items-center justify-between pt-2">
                <h2 className={cn('text-lg font-semibold flex items-center gap-2', titleColor)}>
                  <Users className="h-5 w-5 text-indigo-500" />
                  {t('manager.analytics.leaderboardTitle')}
                </h2>
                <span className={cn('text-xs', subtitleColor)}>
                  {t('manager.analytics.activeAgentsCount', {
                    count: data.agents.length,
                  })}
                </span>
              </div>

              {data.agents.length === 0 ? (
                <div
                  className={cn(
                    'rounded-xl border p-10 text-center',
                    darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white',
                  )}
                >
                  <p className={cn('text-sm', subtitleColor)}>
                    {t('manager.analytics.emptyState')}
                  </p>
                </div>
              ) : (
                <AgentLeaderboardTable
                  agents={data.agents}
                  period={period}
                  darkMode={darkMode}
                  onAgentClick={openAgentPage}
                />
              )}

              {/* Heatmap horaire — vue staffing : à quelle heure on est tannés
                  et où sont les pics de manqués. */}
              <TeamHeatmap heatmap={data.hourly_heatmap} darkMode={darkMode} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
